import React, { useState } from 'react';
import { GameProvider, useGame } from './state/gameStore.tsx';
import { Header } from './components/Header.tsx';
import { Navigation, TabType } from './components/Navigation.tsx';
import { BaseView } from './components/BaseView.tsx';
import { ResearchView } from './components/ResearchView.tsx';
import { ShipyardView } from './components/ShipyardView.tsx';
import { FleetView } from './components/FleetView.tsx';
import { CombatView } from './components/CombatView.tsx';
import { GalaxyMapView } from './components/GalaxyMapView.tsx';
import { FleetMovementBar } from './components/FleetMovementBar.tsx';
import { AllianceView } from './components/AllianceView.tsx';
import { StarsShopView } from './components/StarsShopView.tsx';
import { DailyRewardsAndBoostsView } from './components/DailyRewardsAndBoostsView.tsx';
import { getTelegramWebApp } from './telegram/sdk.ts';

const GameContent: React.FC = () => {
  const { state, loading, error, notification, authStatus, authError, retryAuth } = useGame();
  const [activeTab, setActiveTab] = useState<TabType>('base');

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: 16,
      }}>
        <div style={{
          width: 48,
          height: 48,
          border: '3px solid var(--border-panel)',
          borderTop: '3px solid var(--accent-cyan)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
        <div className="mono" style={{ color: 'var(--accent-cyan)', fontSize: 14, letterSpacing: 1 }}>
          {authStatus === 'authenticating' ? 'ĐANG XÁC THỰC TELEGRAM...' : 'ĐANG KẾT NỐI HỆ THỐNG GALAXYTELEGRAM...'}
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (authStatus === 'expired') {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        padding: 24,
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 44, marginBottom: 12 }}>⚠️</div>
        <h2 style={{ color: 'var(--status-yellow, #eab308)', marginBottom: 8, fontSize: 18 }}>
          PHIÊN ĐĂNG NHẬP TELEGRAM HẾT HẠN
        </h2>
        <p style={{ color: 'var(--text-dim)', fontSize: 13, marginBottom: 20, maxWidth: 320, lineHeight: 1.5 }}>
          {authError || 'Chữ ký xác thực Telegram đã hết hiệu lực (> 24 giờ). Vui lòng đóng và mở lại Mini App từ bot @GO2local_bot để làm mới phiên.'}
        </p>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="sci-btn" onClick={() => retryAuth()}>
            THỬ LẠI
          </button>
          <button
            className="sci-btn"
            style={{ background: 'transparent', borderColor: 'var(--border-panel)' }}
            onClick={() => {
              const tg = getTelegramWebApp();
              if (tg && typeof tg.close === 'function') tg.close();
              else window.location.reload();
            }}
          >
            ĐÓNG MINI APP
          </button>
        </div>
      </div>
    );
  }

  if (error && !state) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        padding: 24,
        textAlign: 'center',
      }}>
        <h2 style={{ color: 'var(--status-red)', marginBottom: 8 }}>MẤT KẾT NỐI MÁY CHỦ</h2>
        <p style={{ color: 'var(--text-dim)', fontSize: 13, marginBottom: 16 }}>{error}</p>
        <button className="sci-btn" onClick={() => retryAuth()}>
          TẢI LẠI ỨNG DỤNG
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', position: 'relative' }}>
      {/* Toast Notification - Floating above bottom navigation */}
      {notification && (
        <div style={{
          position: 'fixed',
          bottom: 70,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(10, 17, 36, 0.95)',
          border: '1px solid var(--accent-cyan)',
          boxShadow: '0 0 16px rgba(0, 229, 255, 0.4)',
          color: 'var(--text-bright)',
          padding: '8px 16px',
          borderRadius: 4,
          fontSize: 12,
          fontWeight: 700,
          zIndex: 1500,
          maxWidth: '92%',
          textAlign: 'center',
          backdropFilter: 'blur(8px)',
          animation: 'fadeIn 0.2s ease',
        }}>
          🔔 {notification}
        </div>
      )}

      {/* Header */}
      <Header />

      {/* Active Fleet Movements Bar */}
      <FleetMovementBar />

      {/* Main View Area with adequate bottom clearance */}
      <main style={{ flex: 1, paddingBottom: 85 }}>
        {activeTab === 'base' && <BaseView />}
        {activeTab === 'galaxy' && <GalaxyMapView />}
        {activeTab === 'fleet' && <FleetView />}
        {activeTab === 'combat' && <CombatView />}
        {activeTab === 'shipyard' && <ShipyardView />}
        {activeTab === 'research' && <ResearchView />}
        {activeTab === 'alliance' && <AllianceView />}
        {activeTab === 'daily' && <DailyRewardsAndBoostsView onOpenShop={() => setActiveTab('shop')} />}
        {activeTab === 'shop' && <StarsShopView />}
      </main>

      {/* Bottom Navigation */}
      <Navigation activeTab={activeTab} onSelectTab={setActiveTab} />
    </div>
  );
};

export const App: React.FC = () => {
  return (
    <GameProvider>
      <GameContent />
    </GameProvider>
  );
};

export default App;
